/**
 * The short-URL boundary (KPE-002, KPR-206): the address bar shows a record's
 * short URL (`/i/7k3qx9m`), the router works on the long one
 * (`/collections/<id>/items/<id>`), and nothing between them notices.
 *
 * createBrowserRouter takes a `window` (react-router's `opts.window`); its
 * history reads `window.location` and writes `window.history.pushState /
 * replaceState`, and listens for `popstate`. The window built here passes
 * every one of those through, translating at the edge:
 *
 *   - READ  (`location.pathname`, on load and on Back / Forward): a short path
 *     the dictionary knows reads as its long path, so the router — every
 *     route, `useParams`, id comparison and storage key in the app — only
 *     ever sees ObjectIds. A short path it does not know reads as itself and
 *     lands on the short routes (pages/ShortRecordRoute.js), which ask the
 *     API and navigate to the long path.
 *   - WRITE (`pushState` / `replaceState`): a long path is written to the
 *     address bar in its short form when every code it needs is known.
 *
 * Two more pieces finish it:
 *   - `wrapCreateHref(router)` — every `<Link>`'s href is short too, so
 *     right-click › Copy link and a middle-click give the short URL; a plain
 *     click still navigates by the link's long `to`.
 *   - `canonicalize()` — when the dictionary learns a code (the record on
 *     screen has loaded), a long URL already in the address bar is replaced
 *     in place with its short form. The router's own location is untouched:
 *     reading the short form back through this window gives the same long
 *     path it already holds.
 *
 * utils/recordCodes.js is the dictionary.
 */
import { learnFrom, subscribe, toLongPath, toShortPath } from './recordCodes';

/** A path string (`/a?b#c`) or a full same-origin URL → the same with its path translated. */
function translateUrl(url, translate) {
	if (url === undefined || url === null || url === '') return url;
	const s = String(url);
	if (s.startsWith('/')) return translate(s);
	try {
		const parsed = new URL(s, window.location.origin);
		if (parsed.origin !== window.location.origin) return url;
		return translate(`${parsed.pathname}${parsed.search}${parsed.hash}`);
	} catch {
		return url;
	}
}

export function createShortUrlWindow(win = window) {
	const location = {
		get pathname() {
			const { pathname } = win.location;
			const long = toLongPath(pathname);
			return long === pathname ? pathname : long.replace(/[?#].*$/, '');
		},
		get search() { return win.location.search; },
		get hash() { return win.location.hash; },
		get origin() { return win.location.origin; },
		get href() { return win.location.href; },
		get host() { return win.location.host; },
		get hostname() { return win.location.hostname; },
		get protocol() { return win.location.protocol; },
		assign: url => win.location.assign(url),
		replace: url => win.location.replace(url),
		reload: () => win.location.reload(),
		toString: () => win.location.toString(),
	};
	const history = {
		get state() { return win.history.state; },
		get length() { return win.history.length; },
		get scrollRestoration() { return win.history.scrollRestoration; },
		set scrollRestoration(v) { win.history.scrollRestoration = v; },
		pushState: (state, title, url) => win.history.pushState(state, title, translateUrl(url, toShortPath)),
		replaceState: (state, title, url) => (url === undefined
			? win.history.replaceState(state, title)
			: win.history.replaceState(state, title, translateUrl(url, toShortPath))),
		go: n => win.history.go(n),
		back: () => win.history.back(),
		forward: () => win.history.forward(),
	};
	return {
		location,
		history,
		document: win.document,
		get sessionStorage() { return win.sessionStorage; },
		get localStorage() { return win.localStorage; },
		addEventListener: (...args) => win.addEventListener(...args),
		removeEventListener: (...args) => win.removeEventListener(...args),
		scrollTo: (...args) => win.scrollTo(...args),
		get scrollX() { return win.scrollX; },
		get scrollY() { return win.scrollY; },
	};
}

/** Every href the router makes, in its short form when the codes are known. */
export function wrapCreateHref(router) {
	const original = router.createHref.bind(router);
	router.createHref = location => toShortPath(original(location));
	return router;
}

/**
 * The address bar's path, shortened in place when the dictionary can. A
 * replaceState of the same history entry with the same state: no navigation,
 * no new entry, and the router — which reads this URL back as the long path
 * it already holds — is none the wiser.
 */
export function canonicalize(win = window) {
	try {
		const { pathname, search, hash } = win.location;
		const current = `${pathname}${search}${hash}`;
		const short = toShortPath(current);
		if (short !== current) win.history.replaceState(win.history.state, '', short);
	} catch {
		// Never let the cosmetic half break a page.
	}
}

/** Wire the dictionary to the address bar and every API answer. Returns an unsubscribe. */
export function installShortUrls({ api, win = window } = {}) {
	const off = subscribe(() => canonicalize(win));
	let eject = null;
	if (api && api.interceptors) {
		const id = api.interceptors.response.use(response => {
			if (response && response.data) learnFrom(response.data);
			return response;
		});
		eject = () => api.interceptors.response.eject(id);
	}
	canonicalize(win);
	return () => { off(); if (eject) eject(); };
}
